import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { SignUpUserRequest, SignUpUserResponse, UserResponse } from 'api';

export const CURRENT_USER_QUERY_KEY = 'vestra/current-user';
export const USER_SIGN_UP_NOW_QUERY_KEY = 'vestra/user-sign-up-now';

export const useSignUpUserMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: SignUpUserResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SignUpUserRequest) => {
      const response = await withErrorHandling(api.v1.signUpUserV1UsersPost(payload), {
        loading: 'Creating an account...',
        success: 'User created!',
        setErrors,
      });

      return response.data;
    },
    onSuccess: (data: SignUpUserResponse) => {
      queryClient.setQueryData<UserResponse>([CURRENT_USER_QUERY_KEY], data.user);
      queryClient.setQueryData<boolean>([USER_SIGN_UP_NOW_QUERY_KEY], true);

      onSuccess && onSuccess(data);
    },
  });
};

export const useRegisterSsoInvestorMutation = () => {
  return useMutation({
    mutationFn: async (apply_register_private_id: string) => {
      const response = await withErrorHandling(
        api.instance.post('/v1/users/register-sso-investor', { apply_register_private_id }),
        { loading: 'Setting up your account...', success: '' },
      );
      return response.data;
    },
  });
};

export const useCurrentUserQuery = (options?: {
  disabled?: boolean;
  onSuccess?: (user: UserResponse) => void;
}) => {
  const { disabled = false, onSuccess } = options || {};
  return useQuery<UserResponse>({
    queryKey: [CURRENT_USER_QUERY_KEY],
    staleTime: 1000 * 60 * 60, // 1 hours
    refetchInterval: 1000 * 60 * 5, //5min
    retry: (failureCount, error) => (error as any)?.response?.status === 404 && failureCount < 3,
    retryDelay: 2500,
    queryFn: async () => {
      const response = await api.v1.getCurrentUserV1UsersCurrentGet();
      return response.data.user;
    },
    onSuccess,
    enabled: !disabled,
  });
};
